// ===== Staff QR Page =====
import React, { useState, useRef, useEffect } from 'react';
import VenueDashboardHeader from '../components/VenueDashboardHeader';
import { useVenueDashboard } from '../contexts/VenueDashboardContext';
import { QrCodeIcon, ArrowDownTrayIcon, ClockIcon } from '@heroicons/react/24/outline';

const StaffQRPage = () => {
  const { canAccess, venue, currentRole } = useVenueDashboard();
  const [qrGenerated, setQrGenerated] = useState(false);
  const [expiresIn, setExpiresIn] = useState(60); // 60 minutes
  const canvasRef = useRef(null);

  useEffect(() => {
    if (qrGenerated && expiresIn > 0) {
      const timer = setInterval(() => {
        setExpiresIn(prev => prev - 1);
      }, 60000); // Update every minute
      return () => clearInterval(timer);
    }
  }, [qrGenerated, expiresIn]);

  const generateQR = () => {
    setQrGenerated(true);
    setExpiresIn(60);
    
    // Draw a mock QR code on canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      const size = 200;
      canvasRef.current.width = size;
      canvasRef.current.height = size;
      
      // White background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, size, size);
      
      // Generate mock QR pattern
      ctx.fillStyle = 'black';
      const cellSize = size / 25;
      for (let i = 0; i < 25; i++) {
        for (let j = 0; j < 25; j++) {
          if (Math.random() > 0.5 || 
              (i < 7 && j < 7) || 
              (i < 7 && j > 17) || 
              (i > 17 && j < 7)) {
            ctx.fillRect(i * cellSize, j * cellSize, cellSize, cellSize);
          }
        }
      }
      
      // Position patterns (corners)
      const drawPositionPattern = (x, y) => {
        ctx.fillStyle = 'black';
        ctx.fillRect(x, y, 7 * cellSize, 7 * cellSize);
        ctx.fillStyle = 'white';
        ctx.fillRect(x + cellSize, y + cellSize, 5 * cellSize, 5 * cellSize);
        ctx.fillStyle = 'black';
        ctx.fillRect(x + 2 * cellSize, y + 2 * cellSize, 3 * cellSize, 3 * cellSize);
      };
      
      drawPositionPattern(0, 0);
      drawPositionPattern(18 * cellSize, 0);
      drawPositionPattern(0, 18 * cellSize);
    }
  };

  const downloadQR = () => {
    if (canvasRef.current) {
      const link = document.createElement('a');
      link.download = `staff-qr-${Date.now()}.png`;
      link.href = canvasRef.current.toDataURL('image/png');
      link.click();
    }
  };

  if (!canAccess(['STAFF', 'MANAGER', 'OWNER', 'ADMIN'])) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <VenueDashboardHeader title="Staff QR" />
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p style={{ color: '#ef4444' }}>Access denied. Staff role or higher required.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <VenueDashboardHeader title="Staff QR Code" />
      
      <div style={{ padding: '20px', maxWidth: '500px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px 24px',
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          {!qrGenerated ? (
            <>
              <QrCodeIcon style={{ width: '64px', height: '64px', color: '#8b5cf6', margin: '0 auto 20px' }} />
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', margin: '0 0 8px 0' }}>
                Generate Staff QR
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '8px' }}>
                Create a QR code to verify your staff status
              </p>
              <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '24px' }}>
                Venue: {venue?.name || 'Chalong Interview Condominium'}
              </p>
              <button
                onClick={generateQR}
                style={{
                  padding: '14px 32px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: '0 auto'
                }}
                data-testid="generate-qr-button"
              >
                <QrCodeIcon style={{ width: '20px', height: '20px' }} />
                Generate QR Code
              </button>
            </>
          ) : (
            <>
              <div style={{
                width: '220px',
                height: '220px',
                margin: '0 auto 20px',
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}>
                <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '16px',
                color: expiresIn < 10 ? '#ef4444' : '#6b7280'
              }}>
                <ClockIcon style={{ width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px' }}>
                  Expires in {expiresIn} minutes
                </span>
              </div>
              
              <div style={{
                backgroundColor: '#f3f4f6',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px'
              }}>
                <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>
                  <strong>Role:</strong> {currentRole}
                </p>
                <p style={{ fontSize: '13px', color: '#374151', margin: '4px 0 0 0' }}>
                  <strong>Venue:</strong> {venue?.name?.slice(0, 25)}...
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={downloadQR}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <ArrowDownTrayIcon style={{ width: '18px', height: '18px' }} />
                  Download
                </button>
                <button
                  onClick={generateQR}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#8b5cf6',
                    color: 'white',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Regenerate
                </button>
              </div>
            </>
          )}
        </div>

        {/* Info Card */}
        <div style={{
          marginTop: '24px',
          backgroundColor: '#eff6ff',
          borderRadius: '12px',
          padding: '16px',
          borderLeft: '4px solid #3b82f6'
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af', margin: '0 0 8px 0' }}>
            ℹ️ How to use
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#1e3a8a', fontSize: '13px' }}>
            <li>Show this QR code when asked by customers</li>
            <li>QR code expires after 60 minutes</li>
            <li>Regenerate if expired</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StaffQRPage;
